import { convertGramsToPounds, convertLengthToCM, convertLengthToFeetAndInches, convertPoundsToGrams, separateDecimal } from '@vestico/utils';

import { BraSizeSource, BraSizeSourcesSorted, getDefaultHeightUnit, getDefaultWeightUnit } from '@vestico/model';
import { AgeUnit, HeightUnit, WeightUnit } from '@vestico/model';

import { getResolvedLocale, i18n } from '@vestico/i18n';

import { BraSelectConfiguration } from '../components/BraSelect';
import { EntryFieldConfiguration } from '../components/EntryField';

export const updateTranslationForConfiguration = (
  existingConfig: EntryFieldConfiguration,
  unit: HeightUnit | WeightUnit | undefined,
  defaultConfig: (unit: HeightUnit | WeightUnit | undefined) => EntryFieldConfiguration
) => {
  const newConfig = defaultConfig(unit);
  const { selectedOptionIndex, value, didUpdateConfig } = existingConfig;
  newConfig.selectedOptionIndex = selectedOptionIndex;
  newConfig.value = value;
  newConfig.didUpdateConfig = didUpdateConfig;
  return newConfig;
};

export const getHeightConfiguration = (unit: HeightUnit | undefined): EntryFieldConfiguration => {
  const defaultUnit = getDefaultHeightUnit() === HeightUnit.feet ? 1 : 0;
  const selectedOptionIndex = unit !== undefined ? unit.valueOf() : defaultUnit;

  return {
    id: 'height',
    name: i18n.t('LOC_BASIC_INFO_TITLE_HEIGHT', { ns: 'shared' }),
    errorText: i18n.t('LOC_BASIC_INFO_ERROR_HEIGHT', { ns: 'shared' }),
    showErrorIfNeeded: false,
    options: [
      {
        name: i18n.t('LOC_BASIC_INFO_UNIT_HEIGHT_CM', { ns: 'shared' }),
        unitType: HeightUnit.centimeter,
        valueConfigs: [{ digitsCount: 3, suffix: i18n.t('LOC_BASIC_INFO_UNIT_HEIGHT_CM', { ns: 'shared' }), maxValue: 280 }],
        standardize: (value: number[]) => {
          return Math.round(value[0]);
        },
        convert: (value: number) => {
          return [Math.round(value)];
        },
      },
      {
        name: i18n.t('LOC_BASIC_INFO_UNIT_HEIGHT_FEET', { ns: 'shared' }),
        unitType: HeightUnit.feet,
        valueConfigs: [
          { digitsCount: 1, suffix: i18n.t('LOC_BASIC_INFO_UNIT_HEIGHT_FEET', { ns: 'shared' }), maxValue: 8 },
          { digitsCount: 2, suffix: i18n.t('LOC_BASIC_INFO_UNIT_HEIGHT_INCHES', { ns: 'shared' }), maxValue: 11 },
        ],
        standardize: (value: number[]) => {
          const feet = value[0];
          const inches = value[1];
          return convertLengthToCM(feet, inches);
        },
        convert: (value: number) => {
          return convertLengthToFeetAndInches(value);
        },
      },
    ],
    selectedOptionIndex,
    minValue: 40,
    maxValue: 280,
    allowOptionsConversion: true,
  };
};

export const getWeightConfiguration = (unit: WeightUnit | undefined): EntryFieldConfiguration => {
  const defaultUnit = getDefaultWeightUnit() === WeightUnit.pound ? 1 : 0;
  const selectedOptionIndex = unit !== undefined ? unit.valueOf() : defaultUnit;

  return {
    id: 'weight',
    name: i18n.t('LOC_BASIC_INFO_TITLE_WEIGHT', { ns: 'shared' }),
    errorText: i18n.t('LOC_BASIC_INFO_ERROR_WEIGHT', { ns: 'shared' }),
    showErrorIfNeeded: false,
    options: [
      {
        name: i18n.t('LOC_BASIC_INFO_UNIT_WEIGHT_KG', { ns: 'shared' }),
        unitType: WeightUnit.kilogram,
        valueConfigs: [{ digitsCount: 3, suffix: i18n.t('LOC_BASIC_INFO_UNIT_WEIGHT_KG', { ns: 'shared' }), maxValue: 300 }],
        standardize: (value: number[]) => {
          return Math.round(value[0] * 1000.0);
        },
        convert: (value: number) => {
          return [Math.round(value / 1000.0)];
        },
      },
      {
        name: i18n.t('LOC_BASIC_INFO_UNIT_WEIGHT_LBS', { ns: 'shared' }),
        unitType: WeightUnit.pound,
        valueConfigs: [{ digitsCount: 3, suffix: i18n.t('LOC_BASIC_INFO_UNIT_WEIGHT_LBS', { ns: 'shared' }), maxValue: 662 }],
        standardize: (value: number[]) => {
          return Math.round(convertPoundsToGrams(value[0]));
        },
        convert: (value: number) => {
          return [Math.round(convertGramsToPounds(value))];
        },
      },
      {
        name: i18n.t('LOC_BASIC_INFO_UNIT_WEIGHT_ST', { ns: 'shared' }),
        unitType: WeightUnit.stone,
        valueConfigs: [
          { digitsCount: 2, suffix: i18n.t('LOC_BASIC_INFO_UNIT_WEIGHT_ST', { ns: 'shared' }), maxValue: 50 },
          { digitsCount: 2, suffix: i18n.t('LOC_BASIC_INFO_UNIT_WEIGHT_LBS', { ns: 'shared' }), maxValue: 13 },
        ],
        standardize: (value: number[]) => {
          const stones = value[0];
          const pounds = value[1];
          const stonesInGrams = stones * 6.35029318 * 1000.0;
          const poundsInGrams = pounds * 453.592;
          const grams = stonesInGrams + poundsInGrams;
          return grams;
        },
        convert: (value: number) => {
          const stones = value / (6.35029318 * 1000.0);
          const stonesDecimal = separateDecimal(stones);
          const stonesValue = stonesDecimal.number;
          const stonesRemainderInPounds = stonesDecimal.decimal * 14;
          return [Math.round(stonesValue), Math.round(stonesRemainderInPounds)];
        },
      },
    ],
    selectedOptionIndex,
    minValue: 40000,
    maxValue: 300000,
    allowOptionsConversion: false,
  };
};

export const getAgeConfiguration = (): EntryFieldConfiguration => {
  return {
    id: 'age',
    name: i18n.t('LOC_BASIC_INFO_TITLE_AGE', { ns: 'shared' }),
    errorText: i18n.t('LOC_BASIC_INFO_ERROR_AGE', { ns: 'shared' }),
    showErrorIfNeeded: false,
    options: [
      {
        name: 'age',
        unitType: AgeUnit.default,
        valueConfigs: [{ digitsCount: 3, maxValue: 200 }],
        standardize: (value: number[]) => {
          return value[0];
        },
        convert: (value: number) => {
          return [value];
        },
      },
    ],
    selectedOptionIndex: 0,
    minValue: 0,
    maxValue: 200,
    allowOptionsConversion: true,
  };
};

export const getBraConfiguration = (i18nLanguage?: string) => {
  const browserCountryCode = getResolvedLocale(i18nLanguage, { showCountryCode: true });
  const firstLocale = browserCountryCode.toLowerCase();
  const options = BraSizeSourcesSorted();
  let selectedOptionIndex = 0;

  options.forEach((braSizeSource: BraSizeSource, index: number) => {
    if (braSizeSource.id === firstLocale) {
      selectedOptionIndex = index;
    }
  });

  const config: BraSelectConfiguration = {
    id: 'bra',
    name: i18n.t('LOC_SELECT_BRA_SIZE_TITLE', { ns: 'shared' }),
    errorText: i18n.t('LOC_SELECT_BRA_SIZE_ERROR', { ns: 'shared' }),
    showErrorIfNeeded: false,
    options: options,
    selectedOptionIndex: selectedOptionIndex,
    value: {},
  };
  return config;
};
